import {
  IconButton,
  InputAdornment,
  TextField as MUITextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import type { BaseFieldProps } from "./FieldProps";
import useIsFieldDisabled from "../../hooks/formHooks/useIsFieldDisabled";
import TypeCrossIcon from "../icons/ui-actions/TypeCrossIcon";

export interface Compare<TFieldValues> {
  lessThan: Path<TFieldValues>;
  message: string;
}

type Props<TFieldValues extends FieldValues> = {
  isNumeric?: boolean;
  compare?: Compare<TFieldValues>;
  isRequired?: boolean;
  isEndAdornment?: boolean;
  limitNumber?: number;
  onCustomChange?: (value: string) => void;
} & BaseFieldProps<TFieldValues>;

const TextField = <TFieldValues extends FieldValues>({
  name,
  label,
  isNumeric,
  disabled = false,
  disabledForEdit = false,
  compare,
  isRequired,
  isEndAdornment = false,
  limitNumber,
  onCustomChange
}: Props<TFieldValues>) => {
  const { control, clearErrors, watch, setError, trigger, register } =
    useFormContext<TFieldValues>();
  const { field, fieldState } = useController({
    name,
    control,
  });
  const errorMessage = fieldState.error?.message;

  const fieldIsDisabled = useIsFieldDisabled(disabled, disabledForEdit);

  const [blurTimeoutId, setBlurTimeoutId] = useState<NodeJS.Timeout>();
  const [isFocused, setIsFocused] = useState(false);
  const watchField = compare?.lessThan && watch(compare?.lessThan);

  useEffect(() => {
    if (compare?.lessThan) {
      const numberWatchValue = Number(watchField);
      if (numberWatchValue > 0 && numberWatchValue < Number(field.value)) {
        setError(field.name, {
          message: compare.message,
        });
      } else {
        clearErrors(field.name);
        trigger(field.name);
      }
    }
  }, [watchField, field.value]);

  const handleClearText = () => {
    field.onChange("" as PathValue<TFieldValues, FieldPath<TFieldValues>>);
  };

  const handleFocus = useCallback(() => {
    clearTimeout(blurTimeoutId);
    setIsFocused(true);
  }, [blurTimeoutId]);

  const handleBlur = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsFocused(false);
    }, 25);
    setBlurTimeoutId(timeoutId);
  }, []);

  const formatNumeric = (v: number) => {
    if (limitNumber) {
      return v > 0 ? Intl.NumberFormat("en-GB").format(v) : "";
    }
    if (v > -1) {
      return Intl.NumberFormat("en-GB").format(v);
    }
    return v;
  };

  const unformatNumeric = (v: string) =>
    Number(v.replace(/\D/g, "")) as PathValue<
      TFieldValues,
      FieldPath<TFieldValues>
    >;

  useEffect(() => {
    if (limitNumber && field.value > -1) {
      const numberString = formatNumeric(field.value).toString();
      const strippedString = numberString.replace(/,/g, "");
      if (strippedString.length > limitNumber && /^\d+$/.test(strippedString)) {
        setError(field.name, {
          message: `Value may not be longer than ${limitNumber} characters.`,
        });
      } else {
        clearErrors(field.name);
        trigger(field.name);
      }
    }
  }, [watchField, field.value]);

  return (
    <MUITextField
      data-testid={`form-${label}-${isNumeric ? "NUMERIC" : "TEXT"}`}
      {...field}
      disabled={fieldIsDisabled}
      onFocus={handleFocus}
      onBlur={handleBlur}
      value={isNumeric ? formatNumeric(field.value) : field.value}
      label={label}
      error={!!errorMessage}
      helperText={errorMessage}
      inputMode={isNumeric ? "numeric" : "text"}
      onChange={e => {
        if(onCustomChange){
          onCustomChange(e.target.value)
        }
        field.onChange(isNumeric ? unformatNumeric(e.target.value) : e);
        clearErrors(name);
      }}
      required={isRequired}
      InputProps={{
        endAdornment: isEndAdornment ? (
          <InputAdornment position="end">%</InputAdornment>
        ) : (isFocused || errorMessage) && field.value ? (
          <InputAdornment position="end">
            <IconButton
              onFocus={handleFocus}
              onBlur={handleBlur}
              edge={"end"}
              onClick={handleClearText}
            >
              <TypeCrossIcon />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

export default TextField;
